/**
 * Toasts — the console's transient feedback for every write (flag flipped, provider
 * reordered, user suspended, KYC resolved). A tiny context with an imperative
 * `push`/helpers, auto-dismissing after a few seconds, dismissible by hand.
 */
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { IconX } from './icons.js';

type ToastKind = 'success' | 'error' | 'info';

interface Toast {
  readonly id: number;
  readonly kind: ToastKind;
  readonly title: string;
  readonly message?: string;
}

interface ToastApi {
  push(kind: ToastKind, title: string, message?: string): void;
  success(title: string, message?: string): void;
  error(title: string, message?: string): void;
  info(title: string, message?: string): void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }): JSX.Element {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const remove = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (kind: ToastKind, title: string, message?: string) => {
      const id = nextId.current++;
      setToasts((prev) => [...prev, { id, kind, title, ...(message ? { message } : {}) }]);
      window.setTimeout(() => remove(id), kind === 'error' ? 6000 : 3800);
    },
    [remove],
  );

  const api = useMemo<ToastApi>(
    () => ({
      push,
      success: (t, m) => push('success', t, m),
      error: (t, m) => push('error', t, m),
      info: (t, m) => push('info', t, m),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toast-host" aria-live="polite" aria-atomic="false">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast--${t.kind}`}>
            <div className="toast__bar" />
            <div className="toast__body">
              <div className="toast__title">{t.title}</div>
              {t.message !== undefined && <div className="toast__msg">{t.message}</div>}
            </div>
            <button className="toast__close" aria-label="Dismiss" onClick={() => remove(t.id)}>
              <IconX size={14} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (ctx === null) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}
